import { useState, useEffect, useRef } from "react";
import styles from "./datosPage.module.css";
import { useAuth } from "../../../../../context/authContext";
import {
  formatApiError,
  pinDigits,
  emptyPinArray,
  getStoredPinLength,
  rememberPinLength,
} from "../../../../../services/api";
import {
  departamentos,
  ciudadesDe,
} from "../../../../../data/colombiaDepartments";
import IdentityVerificationModal from "../../../../../components/identity/IdentityVerificationModal";
import { PinInput, PinLengthToggle } from "../../../../../components/ui";

const Datos = () => {
  const { user, profile, loadProfile, updateProfile, updateProfileMultipart } =
    useAuth();
  const [editando, setEditando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  // PIN actual (para cambiar correo/teléfono): 4 o 6 dígitos, por defecto
  // la última longitud usada en este dispositivo.
  const [pinLength, setPinLength] = useState(getStoredPinLength);
  const [currentPin, setCurrentPin] = useState(() =>
    emptyPinArray(getStoredPinLength())
  );
  const fileInputRef = useRef(null);
  const pinInputRef = useRef(null);

  const handlePinLengthChange = (length) => {
    setPinLength(length);
    setCurrentPin(emptyPinArray(length));
    pinInputRef.current?.focus();
  };

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    identification: "",
    birth_date: "",
    departamento: "",
    ciudad: "",
  });

  const getCurrentUserData = () => {
    const userData = user || {};
    const profileData = profile || {};

    return {
      first_name: profileData.first_name || userData.first_name || "",
      last_name: profileData.last_name || userData.last_name || "",
      email: profileData.email || userData.email || "",
      phone_number: profileData.phone_number || userData.phone_number || "",
      identification:
        profileData.identification || userData.identification || "",
      birth_date: profileData.birth_date || userData.birth_date || "",
      departamento: profileData.departamento || userData.departamento || "",
      ciudad: profileData.ciudad || userData.ciudad || "",
    };
  };

  const photoUrl =
    profile?.profile_photo_url ||
    profile?.profile_photo ||
    user?.profile_photo_url ||
    user?.profile_photo ||
    "";

  const initials = `${(formData.first_name || "?")[0]}${(formData.last_name || "")[0] || ""}`.toUpperCase();

  useEffect(() => {
    setFormData(getCurrentUserData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "departamento") {
        next.ciudad = "";
      }
      return next;
    });
  };

  const emailOrPhoneChanged = () => {
    const current = getCurrentUserData();
    return (
      formData.email.trim() !== (current.email || "").trim() ||
      formData.phone_number.trim() !== (current.phone_number || "").trim()
    );
  };

  const guardarCambios = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const needsPin = emailOrPhoneChanged();
      const pin = pinDigits(currentPin);

      if (needsPin && pin.length !== pinLength) {
        setMessage({
          type: "error",
          text: `Para cambiar el correo o el teléfono debes confirmar tu PIN actual de ${pinLength} dígitos.`,
        });
        setSaving(false);
        return;
      }

      const dataToUpdate = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone_number: formData.phone_number.trim(),
        birth_date: formData.birth_date,
        departamento: formData.departamento,
        ciudad: formData.ciudad,
      };

      if (needsPin) {
        dataToUpdate.current_pin = pin;
      }

      const result = await updateProfile(dataToUpdate);

      if (result.success) {
        setMessage({
          type: "success",
          text: "Datos actualizados correctamente",
        });
        if (needsPin) rememberPinLength(pinLength);
        setEditando(false);
        setCurrentPin(emptyPinArray(pinLength));
        await loadProfile();
      } else {
        setMessage({
          type: "error",
          text: result.error || "Error al guardar",
        });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Error de conexión" });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const cancelarEdicion = () => {
    setEditando(false);
    setMessage({ type: "", text: "" });
    setCurrentPin(emptyPinArray(pinLength));
    setFormData(getCurrentUserData());
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Selecciona una imagen válida" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "La imagen no debe superar 5 MB" });
      return;
    }

    setUploadingPhoto(true);
    setMessage({ type: "", text: "" });
    try {
      const fd = new FormData();
      fd.append("profile_photo", file);
      const result = await updateProfileMultipart(fd);
      if (result.success) {
        setMessage({ type: "success", text: "Foto actualizada" });
        await loadProfile();
      } else {
        setMessage({
          type: "error",
          text: result.error || formatApiError(result) || "Error al subir la foto",
        });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Error de conexión" });
      console.error(err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const isVerified =
    profile?.is_identity_verified ?? user?.is_identity_verified ?? false;
  const verifiedAtRaw =
    profile?.identity_verified_at || user?.identity_verified_at || "";
  const verifiedAt = (() => {
    if (!verifiedAtRaw) return "";
    const d = new Date(verifiedAtRaw);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("es-CO", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
  })();
  const ciudades = ciudadesDe(formData.departamento);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {!editando ? (
          <button
            type="button"
            className={styles.editBtn}
            onClick={() => setEditando(true)}
          >
            Editar
          </button>
        ) : (
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={cancelarEdicion}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={guardarCambios}
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        )}
      </div>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <div className={styles.photoSection}>
        <button
          type="button"
          className={styles.avatarBtn}
          onClick={handlePhotoClick}
          disabled={uploadingPhoto}
          aria-label="Cambiar foto de perfil"
        >
          {photoUrl ? (
            <img src={photoUrl} alt="Foto de perfil" className={styles.avatarImg} />
          ) : (
            <span className={styles.avatarInitials}>{initials}</span>
          )}
          <span className={styles.avatarOverlay}>
            {uploadingPhoto ? "…" : "Cambiar"}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={styles.hiddenFile}
          onChange={handlePhotoChange}
        />
        <div className={styles.photoMeta}>
          <p className={styles.photoHint}>
            Toca para cambiar tu foto de perfil (máx. 5 MB).
          </p>
          {isVerified ? (
            <p className={`${styles.identityStatus} ${styles.identityVerified}`}>
              <span aria-hidden>✓</span> Identidad verificada
              {verifiedAt ? ` el ${verifiedAt}` : ""}
            </p>
          ) : (
            <div className={styles.identityPending}>
              <p className={styles.identityStatus}>
                Verificación de identidad pendiente. Sube las fotos de tu cédula:
                la necesitas para retirar premios y para apostar más de $100.000.
              </p>
              <button
                type="button"
                className={styles.verifyBtn}
                onClick={() => setShowVerifyModal(true)}
              >
                Verificar mi identidad
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.form}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="first_name">Nombre</label>
            <input
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              disabled={!editando}
              placeholder="Tu nombre"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="last_name">Apellido</label>
            <input
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              disabled={!editando}
              placeholder="Tu apellido"
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="email">Correo electrónico</label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled={!editando}
            placeholder="tu@email.com"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="phone_number">Teléfono</label>
            <input
              id="phone_number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              disabled={!editando}
              placeholder="3001234567"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="birth_date">Fecha de nacimiento</label>
            <input
              id="birth_date"
              name="birth_date"
              type="date"
              value={formData.birth_date}
              onChange={handleChange}
              disabled={!editando}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="departamento">Departamento</label>
            <select
              id="departamento"
              name="departamento"
              value={formData.departamento}
              onChange={handleChange}
              disabled={!editando}
              className={!editando ? styles.readOnly : undefined}
            >
              <option value="">Selecciona departamento</option>
              {departamentos.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="ciudad">Ciudad</label>
            <select
              id="ciudad"
              name="ciudad"
              value={formData.ciudad}
              onChange={handleChange}
              disabled={!editando || !formData.departamento}
              className={!editando ? styles.readOnly : undefined}
            >
              <option value="">Selecciona ciudad</option>
              {ciudades.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="identification">Documento de identidad</label>
          <input
            id="identification"
            name="identification"
            value={formData.identification}
            disabled
            className={styles.readOnly}
          />
          <small className={styles.hint}>
            El documento no puede ser modificado
          </small>
        </div>

        {editando && emailOrPhoneChanged() && (
          <div className={styles.pinSection}>
            <label>PIN actual</label>
            <p className={styles.pinHint}>
              Para cambiar el correo o el teléfono, confirma tu PIN (4 o 6
              dígitos). Elige cuántos dígitos tiene.
            </p>
            <PinLengthToggle
              value={pinLength}
              onChange={handlePinLengthChange}
              disabled={saving}
              label="¿Cuántos dígitos tiene tu PIN actual?"
            />
            <PinInput
              ref={pinInputRef}
              length={pinLength}
              value={currentPin}
              onChange={setCurrentPin}
              disabled={saving}
              ariaLabel="PIN actual"
            />
          </div>
        )}
      </div>

      <IdentityVerificationModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
      />
    </div>
  );
};

export default Datos;
